import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faFileMedicalAlt, faSync } from '@fortawesome/free-solid-svg-icons';
import { type Patient, clinicsService } from '../../services/clinicsService';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface ClinicsPatientSummaryModalProps {
  patient: Patient;
  onClose: () => void;
}

const ClinicsPatientSummaryModal: React.FC<ClinicsPatientSummaryModalProps> = ({ patient, onClose }) => {
  const { t } = useTranslation();
  const [summary, setSummary] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(true);

  useEffect(() => {
    generateSummary();
  }, [patient.id]);

  const generateSummary = async () => {
    setIsGenerating(true);
    setSummary(null);
    try {
      const result = await clinicsService.generatePatientSummary(patient.id);
      setSummary(result.summary);
    } catch (err) {
      console.error('Failed to generate summary', err);
      setSummary('Error al generar el resumen del paciente. Por favor, inténtalo de nuevo.');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div data-ui="clinics-patient-summary-modal-region-001" className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div data-ui="clinics-patient-summary-modal-region-002" className="bg-black/30 backdrop-filter backdrop-blur-xl border border-white/20 rounded-3xl w-full max-w-3xl shadow-2xl overflow-hidden flex flex-col h-[80vh]">
        {/* Header */}
        <div data-ui="clinics-patient-summary-modal-region-003" className="flex justify-between items-center p-6 border-b border-white/20 bg-black/30 flex-shrink-0">
          <h3 className="text-xl font-bold text-white flex items-center gap-2">
            <FontAwesomeIcon icon={faFileMedicalAlt} className="clinics-white-shadow-icon text-purple-400" />
            {t('clinics.chat.assistedSummaryTitle')}
          </h3>
          <div data-ui="clinics-patient-summary-modal-region-004" className="flex items-center gap-4">
            <button
              onClick={generateSummary}
              disabled={isGenerating}
              className="text-white/60 hover:text-white transition-colors disabled:opacity-50"
              title={t('clinics.chat.regenerateSummary')}
            >
              <FontAwesomeIcon icon={faSync} className={`clinics-white-shadow-icon ${isGenerating ? 'animate-spin' : ''}`} />
            </button>
            <button
              data-ui="clinics-patient-summary-modal-close"
              onClick={onClose}
              className="w-8 h-8 p-0 flex items-center justify-center bg-black rounded-full text-white/60 hover:text-white transition-colors border border-white/10"
            >
              <FontAwesomeIcon icon={faTimes} className="text-sm" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div data-ui="clinics-patient-summary-modal-region-005" className="p-6 flex-1 overflow-y-auto gemini-scrollbar bg-black-800/50 backdrop-blur-[2px]">
          {isGenerating ? (
            <div data-ui="clinics-patient-summary-modal-region-006" className="h-full flex flex-col items-center justify-center text-white/60 space-y-4">
              <div data-ui="clinics-patient-summary-modal-region-007" className="w-12 h-12 border-4 border-white/10 border-t-purple-500 rounded-full animate-spin"></div>
              <p>{t('clinics.chat.analyzingHistory', { name: patient.full_name })}</p>
            </div>
          ) : (
            <div data-ui="clinics-patient-summary-modal-region-008" className="text-white/90 text-sm leading-relaxed bg-black/20 p-6 rounded-2xl border border-white/5">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  h1: ({ node, ...props }) => <h1 className="text-2xl font-bold mb-4 text-purple-300" {...props} />,
                  h2: ({ node, ...props }) => <h2 className="text-xl font-bold mt-6 mb-3 text-purple-200" {...props} />,
                  h3: ({ node, ...props }) => <h3 className="text-lg font-bold mt-4 mb-2 text-white" {...props} />,
                  p: ({ node, ...props }) => <p className="mb-4 last:mb-0" {...props} />,
                  ul: ({ node, ...props }) => <ul className="list-disc pl-5 mb-4 space-y-1" {...props} />,
                  ol: ({ node, ...props }) => <ol className="list-decimal pl-5 mb-4 space-y-1" {...props} />,
                  li: ({ node, ...props }) => <li className="" {...props} />,
                  strong: ({ node, ...props }) => <strong className="font-bold text-white" {...props} />,
                }}
              >
                {summary || ''}
              </ReactMarkdown>
            </div>
          )}
        </div>

        {/* Footer */}
        <div data-ui="clinics-patient-summary-modal-region-009" className="flex justify-end gap-3 p-6 border-t border-white/20 bg-black/30 flex-shrink-0">
          <button
            data-ui="clinics-patient-summary-modal-cancel"
            onClick={onClose}
            className="btn-taratic px-6 py-2 bg-white/10 border border-white/20 text-white hover:bg-white/20 rounded-full"
          >
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClinicsPatientSummaryModal;
